import { useMemo, useState } from 'react';
import { Flex, StackScreen, useSafeArea } from '@banhala/ui-kit';
import { WebView } from 'shared/src/component/WebWebView';
import { isNotNil } from 'shared/src/utils';
import { useScreensRoute } from '../../hooks/useScreensRoute';
import { useWebViewActionFromMessage } from './hooks/useWebViewActionFromMessage';
import { CartIcon, SearchIcon } from 'shared/src/component';

/**
 * 범용 WebView 화면.
 *
 * `navigation.navigate('WebViewScreen', { url, title })`로 호출.
 * - 앱: react-navigation stack에 push
 * - 웹: history.pushState로 push → `location.href` 방식 대비 기존 스크린 상태 유지
 *
 * 전달받은 url에는 항상 다음 쿼리 파라미터를 주입한다:
 * - `global_layout=app` — 웹페이지가 앱 레이아웃(header/footer 제거)으로 렌더
 * - `title_bar=false`  — StackScreen이 자체 헤더를 제공하므로 웹쪽 타이틀바 숨김
 */
export const WebViewScreen = () => {
  const { params } = useScreensRoute<'WebViewScreen'>();
  const { url, title } = params;
  const [pageTitle, setPageTitle] = useState<string>(title ?? '');
  const { top, bottom } = useSafeArea();
  const webViewAction = useWebViewActionFromMessage();

  const finalUrl = useMemo(() => withWebViewParams(url), [url]);

  return (
    <Flex flex={1} padding={{ top, bottom }}>
      <StackScreen
        title={pageTitle}
        hideHeader={false}
        rightIcons={[<SearchIcon />, <CartIcon />]}
      >
        {isNotNil(finalUrl) && (
          <WebView
            source={{ uri: finalUrl }}
            onMessage={webViewAction}
            onTitleChange={setPageTitle}
          />
        )}
      </StackScreen>
    </Flex>
  );
};

/**
 * url에 in-app WebView용 공통 쿼리 파라미터를 주입.
 * 이미 있으면 덮어쓰고, 파싱 실패 시 원본 반환.
 */
const withWebViewParams = (url: string | undefined): string | undefined => {
  if (!isNotNil(url)) return undefined;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('global_layout', 'app');
    parsed.searchParams.set('title_bar', 'false');
    return parsed.toString();
  } catch {
    return url;
  }
};
